import React from 'react';
import { useParams } from 'react-router-dom';
import useCompetitionStore from 'store/competitionStore';
import RestrictedTabContent from 'components/pages/Competition/CompetitionTabs/RestrictedTabContent';
import { auth, useAuthState } from 'api/services/firebase';
import useIsJoined from 'hooks/useIsJoined';
import Loading from 'components/system/Loading';
import getCompetitionTiming from 'utils/competitionTiming';

const tabRestrictions = {
  problems: {
    requiresLogin: true,
    requiresJoin: true,
    requiresStarted: true,
    allowAfterEnd: true,
  },
  leaderboard: {
    requiresLogin: true,
    requiresJoin: true,
    requiresStarted: true,
    allowAfterEnd: true,
  },
  submissions: {
    requiresLogin: true,
    requiresJoin: true,
    requiresStarted: true,
    allowAfterEnd: true,
  },
};

const withRestrictions = (WrappedComponent) => (props) => {
  const { selectedTab } = props;
  const { id: competitionId } = useParams();
  const [user] = useAuthState(auth);

  const competition = useCompetitionStore((state) => state.competition.details);
  const isCompetitionLoading = useCompetitionStore((state) => state.competition.isLoading);
  const { hasStarted, hasEnded } = getCompetitionTiming(competition);

  const { isJoined, isChecking } = useIsJoined(competitionId);

  const tab = selectedTab.toLowerCase();
  const restrictions = tabRestrictions[tab];

  if (!restrictions) {
    return <WrappedComponent {...props} />; // No restrictions -> allow access
  }

  // Loading states
  if (isCompetitionLoading || isChecking) {
    return <Loading />;
  }

  // Check restrictions in order of priority
  if (restrictions.requiresLogin && !user) {
    return (
      <RestrictedTabContent
        message="You need to log in to access this section."
        showButton
        competition={competition}
      />
    );
  }

  if (restrictions.requiresJoin && !isJoined) {
    return (
      <RestrictedTabContent
        message="You must join the hackathon to view this section."
        showButton
        competition={competition}
      />
    );
  }

  if (restrictions.requiresStarted && !hasStarted) {
    return (
      <RestrictedTabContent
        message="The hackathon has not started yet."
        competition={competition}
      />
    );
  }

  // Handle end-of-competition access
  if (hasEnded) {
    if (!restrictions.allowAfterEnd) {
      return (
        <RestrictedTabContent
          message="This section is no longer accessible as the hackathon has ended."
          competition={competition}
        />
      );
    }
  }

  return <WrappedComponent {...props} />;
};

export default withRestrictions;
